import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { FakeText, Card } from '@totalsoft/rocket-ui';
import { useTranslation } from 'react-i18next';
import { useToast } from 'hooks/toasts/useToast';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { CHECKLIST_QUERY } from '../queries';
import { SAVE_CHECKLIST_USER_GROUPS } from '../mutations';
import { CHECKLIST_X_USER_GROUPS_UPDATED } from '../subscriptions';
import useLanguageTracker from 'hooks/useLanguageTracker';
import ChecklistPropertyListContainer from './checklistProperties/ChecklistPropertyListContainer';
import ChecklistGeneralDataContainer from './generalData/ChecklistGeneralDataContainer';
import ChecklistFiltersContainer from './checklistFilters/ChecklistFiltersContainer';
import UserGroupListContainer from 'features/common/components/userGroup/userGroupList/UserGroupListContainer';

const ChecklistContainer = ({ id, onChangeChecklistTitle, onRedirect }) => {
  const [t] = useTranslation();
  const addToast = useToast();

  const { data, loading, refetch } = useQuery(CHECKLIST_QUERY, {
    variables: { id: id },
    skip: id === undefined || id === null,
    fetchPolicy: 'network-only'
  });
  const onLanguageChange = useCallback(() => {
    refetch();
  }, [refetch]);

  useLanguageTracker({
    onChange: onLanguageChange
  });

  const [saveChecklistXUserGroups] = useMutation(SAVE_CHECKLIST_USER_GROUPS);

  useSubscription(CHECKLIST_X_USER_GROUPS_UPDATED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('Checklist.UserGroupsSavingSucceeded'), 'success');
      client.cache.evict({ id: `ChecklistItem:${data.checklistXUserGroupsUpdated.requiredDocTypeId}` });
      client.cache.gc();
    }
  });

  const handleSaveChecklistXUserGroups = useCallback(
    selectedUserGroups => {
      if (!data?.checklist?.reqDocTypeId) {
        return;
      }
      const userGroups = selectedUserGroups.map(item => {
        return {
          requiredDocTypeId: data.checklist.reqDocTypeId,
          userGroupId: item.userGroupId
        };
      });
      saveChecklistXUserGroups({
        variables: {
          input: { requiredDocTypeXUserGroups: userGroups, requiredDocTypeId: data.checklist.reqDocTypeId }
        }
      });
    },
    [saveChecklistXUserGroups, data?.checklist?.reqDocTypeId]
  );

  return loading ? (
    <Card>
      <FakeText lines={15} />
    </Card>
  ) : (
    <>
      <ChecklistGeneralDataContainer
        id={id}
        data={data?.checklist}
        filters={data && data.checklist ? data.checklist.requiredDocumentFilters : []}
        onChangeChecklistTitle={onChangeChecklistTitle}
        onRedirect={onRedirect}
        refetch={refetch}
      />
      {id !== null && id !== undefined && data?.checklist?.reqDocTypeId !== null && data?.checklist?.reqDocTypeId !== undefined && (
        <>
          <ChecklistPropertyListContainer
            data={data && data.checklist ? data.checklist.requiredDocumentProperties : []}
            reqDocTypeId={data.checklist.reqDocTypeId}
          />
          <ChecklistFiltersContainer
            condition={data && data.checklist ? data?.checklist.filterCondition : null}
            filters={data && data.checklist ? data.checklist.requiredDocumentFilters : []}
            docTypeAreaId={data?.checklist?.id}
          />
          <UserGroupListContainer
            xUserGroups={data?.checklist?.requiredDocTypeXUserGroups || []}
            onXUserGroupsSave={handleSaveChecklistXUserGroups}
            parentLoaded={!loading}
          />
        </>
      )}
    </>
  );
};

ChecklistContainer.propTypes = {
  id: PropTypes.number,
  onChangeChecklistTitle: PropTypes.func,
  onRedirect: PropTypes.func
};

export default ChecklistContainer;
